import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoDirective } from '@jsverse/transloco';
import { alertIcon, errorIcon, infoIcon, successIcon } from '@ld.consumer-portal/data-uri';
import { DocumentsNavigationService } from '@ld.consumer-portal/routing';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';

export type DynamicMessageBannerType = 'warning' | 'info' | 'error' | 'success';

@Component({
  selector: 'ld-dynamic-message-banner',
  imports: [MatIconModule, SvgIconComponent, TranslocoDirective],
  templateUrl: './dynamic-message-banner.component.html',
  styleUrl: './dynamic-message-banner.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicMessageBannerComponent {
  private readonly documentsNavService = inject(DocumentsNavigationService);

  private readonly iconMap: Record<DynamicMessageBannerType, string> = {
    warning: alertIcon,
    info: infoIcon,
    error: errorIcon,
    success: successIcon,
  };

  type = input<DynamicMessageBannerType>('warning');

  title = input<string>('');

  message = input<string>('');

  expandable = input<boolean>(true);

  dismissible = input<boolean>(false);

  bannerId = input<string>('');

  dismissed = output<string>();

  protected readonly isExpanded = signal(false);

  protected readonly iconSrc = computed(() => this.iconMap[this.type()] ?? alertIcon);

  protected readonly styleConfig = computed(() => {
    switch (this.type()) {
      case 'warning':
        return {
          bgClass: 'bg-[#FFFBF2]',
          borderClass: 'border-[#FFAB00]',
        };
      case 'info':
        return {
          bgClass: 'bg-[#EBF2F9]',
          borderClass: 'border-[#0D6BB8]',
        };
      case 'error':
        return {
          bgClass: 'bg-[#FDF6F6]',
          borderClass: 'border-[#CF4452]',
        };
      case 'success':
        return {
          bgClass: 'bg-[#F3FBF8]',
          borderClass: 'border-[#05A678]',
        };
      default:
        return {
          bgClass: 'bg-[#FFFBF2]',
          borderClass: 'border-[#FFAB00]',
        };
    }
  });

  protected toggleExpanded(): void {
    if (this.expandable()) {
      this.isExpanded.update((v) => !v);
    }
  }

  protected onDismiss(event: Event): void {
    event.stopPropagation();
    this.dismissed.emit(this.bannerId());
  }

  protected onBannerClick(event: Event): void {
    const target = event.target as HTMLElement;
    const anchor = target.closest('a');
    if (!anchor) return;

    const href = anchor.getAttribute('href') || '';
    if (href === '#STATEMENTS_AND_DOCUMENTS') {
      event.preventDefault();
      event.stopPropagation();
      this.documentsNavService.navigateToDocuments();
    }
  }
}
